if (!customElements.get('image-carousel')) {
  class ImageCarousel extends HTMLElement {
    connectedCallback() {
      const track = this.querySelector('[data-carousel-track]');
      if (!track) return;

      track.innerHTML = track.innerHTML + track.innerHTML;

      const speed = parseFloat(this.dataset.speed) || 3;
      const autoplay = this.dataset.autoplay !== 'false';
      const slideCount = track.children.length / 2;

      let position = 0;
      let lastTime = null;
      let isPausedByHover = false;
      let pointerDown = false;
      let isDragging = false;
      let dragStartX = 0;
      let dragStartPos = 0;
      let dragDistance = 0;
      let activePointerId = null;
      const CLICK_THRESHOLD = 5;

      let cachedHalfWidth = 0;
      const recalcHalfWidth = () => {
        cachedHalfWidth = track.scrollWidth / 2;
      };
      recalcHalfWidth();

      // Recalc width when images load or viewport resizes (not every frame)
      if ('ResizeObserver' in window) {
        const ro = new ResizeObserver(recalcHalfWidth);
        ro.observe(track);
      } else {
        window.addEventListener('resize', recalcHalfWidth);
      }
      track.querySelectorAll('img').forEach((img) => {
        if (!img.complete) img.addEventListener('load', recalcHalfWidth, { once: true });
      });

      const normalize = (p) => {
        const w = cachedHalfWidth;
        if (w === 0) return 0;
        return ((p % w) + w) % w;
      };
      const apply = () => {
        // translate3d forces GPU compositing; round to avoid subpixel jitter
        track.style.transform = `translate3d(${-Math.round(position)}px, 0, 0)`;
      };

      const step = (t) => {
        if (lastTime === null) lastTime = t;
        // clamp dt so that backgrounded tabs don't cause a big jump on return
        const dt = Math.min((t - lastTime) / 1000, 0.05);
        lastTime = t;

        if (autoplay && !isDragging && !isPausedByHover && cachedHalfWidth > 0) {
          const pxPerSec = cachedHalfWidth / (slideCount * speed);
          position = normalize(position + pxPerSec * dt);
          apply();
        }
        requestAnimationFrame(step);
      };
      requestAnimationFrame(step);

      this.addEventListener('pointerenter', (e) => {
        if (e.pointerType === 'mouse') isPausedByHover = true;
      });
      this.addEventListener('pointerleave', (e) => {
        if (e.pointerType === 'mouse') isPausedByHover = false;
      });

      track.style.cursor = 'grab';
      track.style.touchAction = 'pan-y';
      track.style.userSelect = 'none';

      this.addEventListener('pointerdown', (e) => {
        pointerDown = true;
        isDragging = false;
        dragStartX = e.clientX;
        dragStartPos = position;
        dragDistance = 0;
        activePointerId = e.pointerId;
      });

      this.addEventListener('pointermove', (e) => {
        if (!pointerDown || e.pointerId !== activePointerId) return;
        const delta = e.clientX - dragStartX;
        dragDistance = Math.max(dragDistance, Math.abs(delta));
        if (!isDragging && dragDistance > CLICK_THRESHOLD) {
          isDragging = true;
          track.style.cursor = 'grabbing';
          try {
            this.setPointerCapture(e.pointerId);
          } catch (_) {}
        }
        if (isDragging) {
          position = normalize(dragStartPos - delta);
          apply();
          // when user drags, reset the rAF clock so autoplay resumes smoothly
          lastTime = null;
        }
      });

      const endDrag = (e) => {
        if (!pointerDown || e.pointerId !== activePointerId) return;
        pointerDown = false;
        if (isDragging) {
          isDragging = false;
          track.style.cursor = 'grab';
          try {
            this.releasePointerCapture(e.pointerId);
          } catch (_) {}
        }
        activePointerId = null;
      };
      this.addEventListener('pointerup', endDrag);
      this.addEventListener('pointercancel', endDrag);

      this.addEventListener(
        'click',
        (e) => {
          if (dragDistance > CLICK_THRESHOLD) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
        true
      );

      track.querySelectorAll('img').forEach((img) => {
        img.addEventListener('dragstart', (e) => e.preventDefault());
      });
    }
  }

  customElements.define('image-carousel', ImageCarousel);
}
